import type { AllergyIntolerance } from "@medplum/fhirtypes";
import { ALLERGY_INTOLERANCE_ENTITY_ID_SYSTEM, getTRPCClient } from "@repo/api";
import {
  type AllergyIdInput,
  allergyIdInputSchema,
  allergyIntoleranceIdSchema,
  patientIdSchema,
  type RecordAllergyInput,
  recordAllergyInputSchema,
  type UpdateAllergyInput,
  updateAllergyInputSchema,
} from "@repo/api/schemas";
import type { AllergyIntoleranceId, PatientId } from "@repo/domain";
import {
  ALLERGY_CLINICAL_SYSTEM as CLINICAL_SYSTEM,
  ALLERGY_VERIFICATION_SYSTEM as VER_SYSTEM,
} from "@repo/fhir-systems";
import { eq, useLiveQuery } from "@tanstack/react-db";
import { defineWrite } from "../actions/define-write";
import { useCollection } from "../core/hooks";
import { getDataStoreClient } from "../core/provider";
import { DomainResultError } from "../core/write-errors";
import type { AllergyIntoleranceRow } from "../fhir/types";
import { optimisticPatientReference, patientDomainId } from "./_patient-reference";
import { optimisticPractitionerReference } from "./_practitioner-reference";
import type { CodeableConcept } from "./shared/types";

type AllergyCategory = "food" | "medication" | "environment" | "biologic";

/**
 * AllergyIntolerance — normalized read-model for the patient allergy list.
 * Read-only for now; the record/refute flow arrives with the allergy domain.
 *
 * Note FHIR references the patient via `patient` (not `subject`). `reactions`
 * is flattened to manifestation display strings for a compact rail summary.
 */
export interface AllergyIntoleranceDto {
  allergyIntoleranceId: AllergyIntoleranceId;
  patientId: PatientId;

  code: CodeableConcept;

  clinicalStatus?: "active" | "inactive" | "resolved";
  verificationStatus?: "unconfirmed" | "confirmed" | "refuted" | "entered-in-error";
  type?: "allergy" | "intolerance";
  category?: AllergyCategory[];
  criticality?: "low" | "high" | "unable-to-assess";

  recordedDate?: string;

  /** Manifestation display strings flattened across all reactions. */
  reactions?: string[];
}

const COLLECTION = "allergyIntolerance";

const VALID_CLINICAL: ReadonlySet<string> = new Set(["active", "inactive", "resolved"]);
const VALID_VERIFICATION: ReadonlySet<string> = new Set(["unconfirmed", "confirmed", "refuted", "entered-in-error"]);
const VALID_CATEGORY: ReadonlySet<string> = new Set(["food", "medication", "environment", "biologic"]);
const VALID_CRITICALITY: ReadonlySet<string> = new Set(["low", "high", "unable-to-assess"]);

function flatCodeable(
  c: { coding?: Array<{ system?: string; code?: string; display?: string }>; text?: string } | undefined,
): CodeableConcept | undefined {
  if (!c) return undefined;
  const first = c.coding?.[0];
  const out = {
    ...(first?.system ? { system: first.system } : {}),
    ...(first?.code ? { code: first.code } : {}),
    ...(first?.display ? { display: first.display } : {}),
    ...(c.text ? { text: c.text } : {}),
  };
  return Object.keys(out).length ? out : undefined;
}

function flatStatusCode(
  c: { coding?: Array<{ system?: string; code?: string }> } | undefined,
  expectedSystem: string,
  valid: ReadonlySet<string>,
): string | undefined {
  const code = c?.coding?.find((co) => co.system === expectedSystem)?.code ?? c?.coding?.[0]?.code;
  return code && valid.has(code) ? code : undefined;
}

function flatReactions(reaction: AllergyIntolerance["reaction"]): string[] | undefined {
  const out = (reaction ?? []).flatMap((r) =>
    (r.manifestation ?? [])
      .map((m) => flatCodeable(m)?.display ?? flatCodeable(m)?.text)
      .filter((s): s is string => !!s),
  );
  return out.length ? out : undefined;
}

function toDto(a: AllergyIntoleranceRow): AllergyIntoleranceDto | undefined {
  if (!a.domainId) return undefined;
  const allergyId = allergyIntoleranceIdSchema.safeParse(a.domainId);
  const patientId = patientIdSchema.safeParse(patientDomainId(a.patient));
  if (!allergyId.success || !patientId.success) return undefined;
  const code = flatCodeable(a.code) ?? {};
  const category = (a.category ?? []).filter((c): c is AllergyCategory => VALID_CATEGORY.has(c));
  return {
    allergyIntoleranceId: allergyId.data,
    patientId: patientId.data,
    code,
    ...(flatStatusCode(a.clinicalStatus, CLINICAL_SYSTEM, VALID_CLINICAL)
      ? {
          clinicalStatus: flatStatusCode(
            a.clinicalStatus,
            CLINICAL_SYSTEM,
            VALID_CLINICAL,
          ) as AllergyIntoleranceDto["clinicalStatus"],
        }
      : {}),
    ...(flatStatusCode(a.verificationStatus, VER_SYSTEM, VALID_VERIFICATION)
      ? {
          verificationStatus: flatStatusCode(
            a.verificationStatus,
            VER_SYSTEM,
            VALID_VERIFICATION,
          ) as AllergyIntoleranceDto["verificationStatus"],
        }
      : {}),
    ...(a.type ? { type: a.type } : {}),
    ...(category.length ? { category } : {}),
    ...(a.criticality && VALID_CRITICALITY.has(a.criticality)
      ? { criticality: a.criticality as AllergyIntoleranceDto["criticality"] }
      : {}),
    ...(a.recordedDate ? { recordedDate: a.recordedDate } : {}),
    ...(flatReactions(a.reaction) ? { reactions: flatReactions(a.reaction) } : {}),
  };
}

interface UseAllergiesParams {
  patientId?: string;
}

interface UseAllergiesResult {
  allergies: AllergyIntoleranceDto[];
  isLoading: boolean;
  isReady: boolean;
  isError: boolean;
}

export function useAllergies(params?: UseAllergiesParams): UseAllergiesResult {
  const collection = useCollection(COLLECTION);
  const patientId = params?.patientId;

  const result = useLiveQuery({
    queryKey: [collection.id, "allergies", patientId ?? null],
    query: (q) => {
      if (patientId) {
        return q
          .from({ a: collection })
          .where(({ a }) => eq(a.patient?.identifier?.value, patientId))
          .fn.select(({ a }) => ({ dto: toDto(a) }));
      }
      return undefined;
    },
  });

  return {
    allergies: ((result.data as Array<{ dto?: AllergyIntoleranceDto }> | undefined) ?? []).flatMap((row) =>
      row.dto ? [row.dto] : [],
    ),
    isLoading: result.isLoading,
    isReady: result.isReady,
    isError: result.isError,
  };
}

// ── Write-side intents (optimistic) ──

const allergies = () => getDataStoreClient().getCollection<AllergyIntoleranceRow, string>(COLLECTION);

/** Rows are keyed by domain identity; this only asserts the row is loaded. */
function resourceIdForDomainId(allergyId: AllergyIntoleranceId): string {
  if (!allergies().has(allergyId)) throw new Error(`Allergy '${allergyId}' is not loaded`);
  return allergyId;
}

function clinicalCoding(code: string) {
  return { coding: [{ system: CLINICAL_SYSTEM, code }] };
}
function verificationCoding(code: string) {
  return { coding: [{ system: VER_SYSTEM, code }] };
}

/** Intent: "record a patient allergy/intolerance". */
export const recordAllergy = defineWrite<RecordAllergyInput>({
  optimistic: (params) => {
    const input = recordAllergyInputSchema.parse(params);
    const optimisticAllergyId = input.id;
    const record = {
      resourceType: "AllergyIntolerance",
      identifier: [{ system: ALLERGY_INTOLERANCE_ENTITY_ID_SYSTEM, value: optimisticAllergyId }],
      domainId: optimisticAllergyId,
      code: input.code,
      patient: optimisticPatientReference(input.patient.id),
      clinicalStatus: clinicalCoding(input.clinicalStatus),
      verificationStatus: verificationCoding(input.verificationStatus),
      type: input.type,
      category: input.category,
      criticality: input.criticality,
      recordedDate: input.recordedDate ?? new Date().toISOString(),
      recorder: input.recorder ? optimisticPractitionerReference(input.recorder.id) : undefined,
      reaction: input.reaction,
    } as AllergyIntoleranceRow;
    allergies().insert(record);
  },
  persist: async (params, options) => {
    const result = await getTRPCClient().charting.allergy.record.mutate(params, options);
    if (!result.success) throw new DomainResultError(result.error.message);
  },
});

/** Intent: "resolve an allergy" — clinicalStatus→resolved. */
export const resolveAllergy = defineWrite<AllergyIdInput>({
  optimistic: (params) => {
    const input = allergyIdInputSchema.parse(params);
    const collection = allergies();
    const resourceId = resourceIdForDomainId(input.allergyIntoleranceId);
    const resolved = clinicalCoding("resolved");
    collection.update(resourceId, (draft) => {
      draft.clinicalStatus = resolved;
    });
  },
  persist: async (params, options) => {
    const result = await getTRPCClient().charting.allergy.resolve.mutate(params, options);
    if (!result.success) throw new DomainResultError(result.error.message);
  },
});

/** Intent: "update an allergy's status, criticality, type, category, or reactions". */
export const updateAllergy = defineWrite<UpdateAllergyInput>({
  optimistic: (params) => {
    const input = updateAllergyInputSchema.parse(params);
    const collection = allergies();
    const resourceId = resourceIdForDomainId(input.allergyIntoleranceId);
    const changes: Partial<AllergyIntolerance> = {};
    if (input.clinicalStatus !== undefined) changes.clinicalStatus = clinicalCoding(input.clinicalStatus);
    if (input.verificationStatus !== undefined)
      changes.verificationStatus = verificationCoding(input.verificationStatus);
    if (input.type !== undefined) changes.type = input.type;
    if (input.category !== undefined) changes.category = input.category;
    if (input.criticality !== undefined) changes.criticality = input.criticality;
    if (input.reaction !== undefined) changes.reaction = input.reaction;
    collection.update(resourceId, (draft) => {
      Object.assign(draft, changes);
    });
  },
  persist: async (params, options) => {
    const result = await getTRPCClient().charting.allergy.update.mutate(params, options);
    if (!result.success) throw new DomainResultError(result.error.message);
  },
});
